import React, { useMemo } from "react";

import AvatarIcon from "assets/icons/AvatarIcon";
import type { Student } from "types/Student";

type AvatarStudent = Pick<
  Student,
  "first_name" | "last_name" | "profile_picture_for_admin"
>;

interface StudentAvatarProps {
  student: AvatarStudent | undefined;
  size?: "sm" | "md" | "lg";
  showOnlineStatus?: boolean;
  className?: string;
}

const SIZE_MAP: Record<NonNullable<StudentAvatarProps["size"]>, string> = {
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
};

const StudentAvatar: React.FC<StudentAvatarProps> = React.memo(
  ({ student, size = "md", showOnlineStatus = false, className = "" }) => {
    const sizeClasses = SIZE_MAP[size];

    const initials = useMemo(() => {
      if (!student) return "";
      const first = student.first_name?.[0] || "";
      const last = student.last_name?.[0] || "";
      const combined = `${last}${first}`.trim();
      return combined.toUpperCase() || "?";
    }, [student]);

    const pictureUrl = student?.profile_picture_for_admin || "";

    return (
      <div className={`relative ${className}`}>
        <div
          className={`flex items-center justify-center rounded-full bg-gray-200 text-gray-600 shadow-inner ${sizeClasses}`}
          aria-hidden
        >
          {pictureUrl ? (
            <img
              src={pictureUrl}
              alt={student ? `${student.last_name} ${student.first_name}` : ""}
              className="h-full w-full rounded-full object-cover"
              loading="lazy"
            />
          ) : initials ? (
            <span className="font-semibold">{initials}</span>
          ) : (
            <AvatarIcon
              width={size === "sm" ? 24 : size === "md" ? 32 : 40}
              height={size === "sm" ? 24 : size === "md" ? 32 : 40}
              color="#9CA3AF"
            />
          )}
        </div>
        {showOnlineStatus && (
          <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
        )}
      </div>
    );
  }
);

StudentAvatar.displayName = "StudentAvatar";

export default StudentAvatar;
